import { defineComponent as _defineComponent } from 'vue'
import { onMounted, getCurrentInstance, ref, computed, watch } from 'vue'
import AuthService from '@/services'
import { useOceanStore } from '@/store/ocean'
import { useStore } from '@/store'

import AlertBanner from '@/components/AlertBanner.vue'

import { validateEmail, validatePassword } from '@/utils/validator'
import { debounce } from '@/utils/debouncer'
import { useI18n } from 'vue-i18n-composable'
import { GaAuthMethod, GaAuthType, useGaAuth } from '@/composables/useGaAuth'
import { SignIn, SignUp } from '@/constants'

import { AlphaButtonAv } from '@op-frontend/vue2tifywind';


export default /*#__PURE__*/_defineComponent({
  __name: 'AuthForm',
  props: {
    authType: null,
    redirectUrl: null
  },
  setup(__props: any) {

const props = __props as {
	authType: 'signin' | 'signup'
	redirectUrl: string
};



const authStore = useStore()
const oceanStore = useOceanStore()
const { locale } = useI18n()
const { trackConsentCheckbox, trackSubmitAuth, trackMissingConsents } = useGaAuth(
	props.authType === SignIn ? GaAuthType.SignIn : GaAuthType.SignUp
)

const signInButtonRef = ref<HTMLDivElement | null>(null)
const isShowPassword = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const loginAttempt = ref<number>(0)

const instance = getCurrentInstance()
const experience = (instance?.proxy?.$route?.query?.exp as string) || ''
const formValue = ref({
	email: {
		value: '',
		error: ''
	},
	password: {
		value: '',
		error: ''
	},
	consent: {
		value: false
	}
})

const errorMessageAPI = ref<string>('')

const domain = process.env.VUE_APP_OP_DOMAIN
const url = computed(() => {
	const isEnglish = locale.value === 'en'
	return {
		tnc: isEnglish
			? `https://www.${domain}/en/terms-conditions`
			: `https://www.${domain}/syarat-dan-ketentuan`,
		privacyPolicy: isEnglish
			? `https://www.${domain}/en/privacy-policy`
			: `https://www.${domain}/kebijakan-privasi`
	}
})

const validateEmailValue = () => {
	const email = formValue.value.email.value
	if (!validateEmail(email)) {
		formValue.value.email.error = 'errors.email__invalid'
		return false
	}
	formValue.value.email.error = ''
	return true
}

const validatePasswordValue = () => {
	const password = formValue.value.password.value
	if (!validatePassword(password)) {
		formValue.value.password.error = 'errors.password__invalid-length'
		return false
	}
	formValue.value.password.error = ''
	return true
}
const handlePasswordInput = debounce(validatePasswordValue, 500)

const submitSignIn = async () => {
	if (!validateEmailValue() || !validatePasswordValue()) {
		return
	}

	isLoading.value = true
	try {
		const signInPayload = {
			email: formValue.value.email.value,
			password: formValue.value.password.value,
			redirectUrl: authStore.redirectUrl || props.redirectUrl,

			// ocean field
			ecosystemId: oceanStore.id || '',
			experience
		}
		const { redirectUrl, user_id } = await AuthService.signInAction(signInPayload)
		trackSubmitAuth({
			userId: user_id,
			authMethod: GaAuthMethod.Email,
			isSuccess: true
		})
		window.location.href = redirectUrl
	} catch (err) {
		const errorCode = err.response?.data?.code
		const failState = err.response?.data?.failState
		loginAttempt.value = err.response?.data?.loginChance

		if (failState === 'user') {
			const blockRedirect = err.response?.data?.redirectUrl
			if (blockRedirect) {
				window.location.href = blockRedirect
			} else {
				errorMessageAPI.value = 'errors.email__invalid-credential'
			}
		} else if (failState === 'password') {
			formValue.value.password.error = 'errors.password__fail-attempt'
			errorMessageAPI.value = 'errors.password__fail-attempt'
		} else if (failState === 'locked') {
			formValue.value.password.error = 'errors.password__account-locked'
			errorMessageAPI.value = 'errors.password__account-locked'
		} else if (failState === 'google_signin') {
			errorMessageAPI.value = 'errors.email__belongs_to_google'
		} else if (errorCode === 'MFA_LIMIT_REACHED') {
			errorMessageAPI.value = 'errors.mfa__limit_reached'
		} else {
			errorMessageAPI.value = err.response?.data?.message
		}

		trackSubmitAuth({
			authMethod: GaAuthMethod.Email,
			isSuccess: false,
			message: errorMessageAPI.value
		})
	} finally {
		isLoading.value = false
	}
}

const submitSignUp = async () => {
	const isConsentGiven = formValue.value.consent.value
	if (!isConsentGiven) {
		errorMessageAPI.value = 'errors.tos_not_accepted'
		return
	}

	if (!validateEmailValue() || !validatePasswordValue()) {
		trackMissingConsents(GaAuthMethod.Email)
		return
	}

	isLoading.value = true
	try {
		const signUpPayload = {
			email: formValue.value.email.value,
			password: formValue.value.password.value,
			redirectUrl: authStore.redirectUrl || props.redirectUrl,

			// ocean field
			ecosystemId: oceanStore.id || '',
			experience
		}

		const { redirectUrl, user_id } = await AuthService.signUpAction(signUpPayload)
		trackSubmitAuth({
			userId: user_id,
			authMethod: GaAuthMethod.Email,
			isSuccess: true
		})
		window.location.href = redirectUrl
	} catch (err) {
		const failState = err.response?.data?.failState
		if (failState === 'user') {
			const blockRedirect = err.response?.data?.redirectUrl
			if (blockRedirect) {
				window.location.href = blockRedirect
			}
		}
		const code = err.response?.data?.code
		if (code && code === '11') {
			errorMessageAPI.value = 'errors.email__already-registered'
		}

		trackSubmitAuth({
			authMethod: GaAuthMethod.Email,
			isSuccess: false,
			message: errorMessageAPI.value
		})
	} finally {
		isLoading.value = false
	}
}

const showTosError = () => {
	errorMessageAPI.value = 'errors.tos_not_accepted'
}

const resetErrorMessage = () => {
	errorMessageAPI.value = ''
}

const handleGoogleSSOError = () => {
	const googleErrors = ['attempt', 'user-not-found', 'user-already-registered']
	const queryParam = instance?.proxy?.$route?.query ?? {}
	const errorKey = Object.keys(queryParam).find((k) => k.includes('google-failure')) || ''

	if (errorKey) {
		const errorType = googleErrors.find((errorKey) =>
			Object.keys(queryParam).some((key) => key.includes(errorKey))
		)
		errorMessageAPI.value = `errors.${errorType}`
		trackSubmitAuth({
			authMethod: GaAuthMethod.Google,
			isSuccess: false,
			message: errorMessageAPI.value
		})
	}
}

const resetForm = () => {
	formValue.value.email.value = ''
	formValue.value.email.error = ''
	formValue.value.password.value = ''
	formValue.value.password.error = ''
	formValue.value.consent.value = false
}

watch(
	() => props.authType,
	() => resetForm()
)

const init = (instance: any) => {
	if (!instance || !signInButtonRef.value) return
	const proxy = instance.proxy as any
	let buttonText = 'continue_with'
	if (props.authType === SignUp) {
		buttonText = 'signup_with'
	}
	proxy.$gsi.setEcosystemId(oceanStore.id || '')
	proxy.$gsi.setExperience(experience)
	proxy.$gsi.createWCButton(signInButtonRef.value, proxy.$i18n.locale, buttonText)
	handleGoogleSSOError()
}

onMounted(() => {
	const instance = getCurrentInstance()
	watch(
		() => oceanStore.id,
		() => {
			init(instance)
		}
	)
	init(instance)
})

return { __sfc: true,props, authStore, oceanStore, locale, trackConsentCheckbox, trackSubmitAuth, trackMissingConsents, signInButtonRef, isShowPassword, isLoading, loginAttempt, instance, experience, formValue, errorMessageAPI, domain, url, validateEmailValue, validatePasswordValue, handlePasswordInput, submitSignIn, submitSignUp, showTosError, resetErrorMessage, handleGoogleSSOError, resetForm, init, AlertBanner, SignIn, SignUp, AlphaButtonAv }
}

})